import type { Draft } from 'immer';
import type { CardEffect } from '../../core/effect';
import { effectId, type CardTitle, type CardUid } from '../../core/ids';
import { shuffle } from '../../core/shuffle';
import type { GameState } from '../../core/state';
import { consumeTurn, passToNext } from '../../core/turns';
import { VANILLA_PACK_ID } from './id';

/**
 * 原版包提供的卡牌效果实现。
 *
 * ⚠️ 这些函数**只被 `core/reducer.ts` 按 ID 查出来调用**，
 * 核心代码里没有任何一处认识 `vanilla:hash` 这类字符串。
 * 扩展包在 `src/packs/<name>/effects.ts` 里用同样方式注册自己的效果即可。
 *
 * 每个函数都必须：同步、纯（只改 draft）、不读时钟、不用 `Math.random`。
 */

/** 看牌堆顶几张 */
const PEEK_COUNT = 3;

function titleOf(draft: Draft<GameState>, uid: CardUid): CardTitle | undefined {
  return draft.cards[uid]?.title;
}

/** Signal 信号：不抽牌结束本回合，令下家连做两个回合 */
const signal: CardEffect = (draft, events, ctx) => {
  const current = draft.turn;
  // 官方 2 → 4 → 6 的叠加规则，推导见 core/turns.ts 的注释
  const base = current !== null && current.fromAttack ? current.turnsRemaining : 0;
  const turns = base + 2;

  passToNext(draft, events, {
    turnsRemaining: turns,
    fromAttack: true,
    // ★ 记下来源牌：结束键按**这张牌的颜色**染色（见 core/state.ts 的 TurnSlot）
    attackSource: ctx.card.id,
  });

  const next = draft.turn;
  if (next !== null) events.push({ t: 'turn/attacked', player: next.player, turns });
};

/** Suspend 挂起：不抽牌结束本回合。被攻击时只能抵消其中一次 */
const suspend: CardEffect = (draft, events) => {
  consumeTurn(draft, events);
};

/** Await 求值：指定一名玩家，他自行选一张手牌给你 */
const await_: CardEffect = (draft, events, ctx) => {
  const target = ctx.play.target;
  if (target === null || target === ctx.actor) return;

  const targetPlayer = draft.players.find((p) => p.id === target);
  // 手上没牌就没什么可给的。官方只说"必须给你一张"，
  // **给不出**的时候（空手）自然什么都不发生。
  //
  // ⚠️ 不检查这一条会**把整局卡死**：会设出一个"无解"的抉择 ——
  // 该玩家没有任何合法动作，reducer 也不会自己跳过它。
  // （这条是 `npm run sim` 撞出来的。）
  if (targetPlayer === undefined || targetPlayer.hand.length === 0) return;

  draft.decision = {
    kind: 'giveCard',
    player: target,
    to: ctx.actor,
    namedTitle: null, // 随便挑
  };
  void events;
};

/** Hash 哈希：彻底洗乱抽牌堆 */
const hash: CardEffect = (draft, events) => {
  const result = shuffle(draft.drawPile, draft.rng);
  draft.drawPile = [...result.items];
  draft.rng = result.rng;

  // ★ 牌序被打乱了：**定位降级为区域**（不是清空）。Hash 只洗抽牌堆，
  //   不会把牌洗进任何人的手牌 —— 所以「这几张还在抽牌堆里」是确定信息。
  for (const key of Object.keys(draft.knowledge)) {
    const known = draft.knowledge[key];
    if (known === undefined) continue;
    for (const card of known.knownTop) {
      const found = known.knownInDraw.find((entry) => entry.title === card.title);
      if (found !== undefined) found.count += 1;
      else known.knownInDraw.push({ title: card.title, count: 1 });
    }
    known.knownTop = [];
  }

  events.push({ t: 'pile/shuffled' });
};

/** Debug 调试：私下查看牌堆顶 3 张，按原顺序放回 */
const debug: CardEffect = (draft, events, ctx) => {
  const knownTop: { indexFromTop: number; title: CardTitle }[] = [];

  // drawPile 的**末尾是堆顶**
  for (let offset = 0; offset < PEEK_COUNT; offset++) {
    const uid = draft.drawPile[draft.drawPile.length - 1 - offset];
    if (uid === undefined) break;
    const title = titleOf(draft, uid);
    if (title === undefined) continue;
    knownTop.push({ indexFromTop: offset, title });
  }

  // ⚠️ 只替换「定位」那一层；已有的区域知识（洗牌后残留）要保留
  const existing = draft.knowledge[ctx.actor];
  if (existing === undefined) {
    draft.knowledge[ctx.actor] = { knownTop, knownInDraw: [], knownInHand: [] };
  } else {
    existing.knownTop = knownTop;
  }
  // ⚠️ 看到了什么不进事件 —— 事件是广播给所有人的
  events.push({ t: 'pile/peeked', player: ctx.actor, count: knownTop.length });
};

/** 原版包的全部效果，按 `vanilla:<名字>` 注册 */
export const coreEffects: Readonly<Record<string, CardEffect>> = {
  [effectId(VANILLA_PACK_ID, 'signal')]: signal,
  [effectId(VANILLA_PACK_ID, 'suspend')]: suspend,
  [effectId(VANILLA_PACK_ID, 'await')]: await_,
  [effectId(VANILLA_PACK_ID, 'hash')]: hash,
  [effectId(VANILLA_PACK_ID, 'debug')]: debug,
};
